import React, { useState, useEffect } from 'react';
import UniversalNavbar from '../components/UniversalNavbar';

interface Assignment {
  _id: string;
  customerEmail: string;
  customerName?: string;
  treesOrdered: number;
  trees?: number; // Alias for treesOrdered
  location?: string;
  status: 'assigned' | 'planted' | 'completed';
  assignedDate?: string;
  assignedAt?: string; // Alias for assignedDate
  plantingDate?: string;
  completedAt?: string;
  photos?: string[];
  notes?: string;
  adminNotes?: string;
}

interface PhotoUpdate {
  packageId: string;
  packageType: string;
  scheduleIndex: number;
  scheduledDate: string;
  description: string;
  status: 'pending' | 'assigned' | 'completed' | 'overdue';
  photoFilename?: string;
  originalOrder: {
    _id: string;
    treesOrdered: number;
    customerEmail: string;
  };
  customerEmail: string;
}

interface WorkerStats {
  totalAssignments: number;
  treesPlanted: number;
  completedOrders: number;
  pendingAssignments: number;
}

const WorkerPortal: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [workerInfo, setWorkerInfo] = useState<{ name: string; email: string; workerId: string } | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [photoUpdates, setPhotoUpdates] = useState<PhotoUpdate[]>([]);
  const [stats, setStats] = useState<WorkerStats>({ totalAssignments: 0, treesPlanted: 0, completedOrders: 0, pendingAssignments: 0 });
  const [loading, setLoading] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState<string | null>(null);
  const [uploadingPhotoUpdate, setUploadingPhotoUpdate] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<{ [key: string]: File[] }>({});
  const [previewUrls, setPreviewUrls] = useState<{ [key: string]: string[] }>({});
  const [photoUpdateFiles, setPhotoUpdateFiles] = useState<{ [key: string]: File | null }>({});
  const [photoUpdatePreviews, setPhotoUpdatePreviews] = useState<{ [key: string]: string }>({});

  // GPS coordinates state
  const [showGpsModal, setShowGpsModal] = useState(false);
  const [selectedOrderForPlanting, setSelectedOrderForPlanting] = useState<string | null>(null);
  const [gpsCoordinates, setGpsCoordinates] = useState<{ latitude: string; longitude: string }>({ latitude: '', longitude: '' });
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);

  // Login form state
  const [loginData, setLoginData] = useState({
    workerId: '',
    password: ''
  });

  useEffect(() => {
    // Check if already authenticated
    const token = localStorage.getItem('workerToken');
    if (token) {
      verifyToken(token);
    }
  }, []);

  const verifyToken = async (token: string) => {
    try {
      const response = await fetch('/api/auth/worker/verify', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setWorkerInfo(data.worker);
        loadWorkerData();
      } else {
        localStorage.removeItem('workerToken');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('workerToken');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('/api/auth/worker/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: loginData.workerId, // Worker login uses email field
          password: loginData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('workerToken', data.token);
        setIsAuthenticated(true);
        setWorkerInfo(data.user);
        loadWorkerData();
      } else {
        alert(data.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkerData = async () => {
    try {
      const token = localStorage.getItem('workerToken');

      // Load assignments
      const ordersResponse = await fetch('/api/workers/orders', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (ordersResponse.ok) {
        const data = await ordersResponse.json();
        setAssignments(data.orders || []);

        // Calculate stats
        const totalAssignments = data.orders.length;
        const treesPlanted = data.orders
          .filter((assignment: Assignment) => assignment.status === 'planted' || assignment.status === 'completed')
          .reduce((sum: number, assignment: Assignment) => sum + (assignment.treesOrdered || assignment.trees || 0), 0);
        const completedOrders = data.orders.filter((assignment: Assignment) => assignment.status === 'completed').length;
        const pendingAssignments = data.orders.filter((assignment: Assignment) => assignment.status === 'assigned').length;

        setStats({
          totalAssignments,
          treesPlanted,
          completedOrders,
          pendingAssignments
        });
      }

      // Load photo updates
      const photoResponse = await fetch('/api/workers/photo-updates', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (photoResponse.ok) {
        const photoData = await photoResponse.json();
        setPhotoUpdates(photoData.photoUpdates || []);
      }
    } catch (error) {
      console.error('Failed to load worker data:', error);
    }
  };

  // Get current GPS location
  const getCurrentLocation = () => {
    setGpsLoading(true);
    setGpsError(null);

    if (!navigator.geolocation) {
      setGpsError('Geolocation is not supported by your browser');
      setGpsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsCoordinates({
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        });
        setGpsLoading(false);
        setGpsError(null);
      },
      (error) => {
        setGpsError(`Unable to get location: ${error.message}`);
        setGpsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Open GPS modal for marking as planted
  const openGpsModalForPlanting = (assignmentId: string) => {
    setSelectedOrderForPlanting(assignmentId);
    setShowGpsModal(true);
    setGpsCoordinates({ latitude: '', longitude: '' });
    setGpsError(null);
  };

  // Submit planting with GPS coordinates
  const submitPlantingWithGps = async () => {
    if (!selectedOrderForPlanting) return;

    if (!gpsCoordinates.latitude || !gpsCoordinates.longitude) {
      alert('Please provide GPS coordinates');
      return;
    }

    try {
      const token = localStorage.getItem('workerToken');
      const response = await fetch(`/api/workers/orders/${selectedOrderForPlanting}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'planted',
          latitude: parseFloat(gpsCoordinates.latitude),
          longitude: parseFloat(gpsCoordinates.longitude)
        })
      });

      if (response.ok) {
        setShowGpsModal(false);
        setSelectedOrderForPlanting(null);
        setGpsCoordinates({ latitude: '', longitude: '' });
        loadWorkerData(); // Reload data
        alert('Order marked as planted with GPS coordinates!');
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to mark as planted');
      }
    } catch (error) {
      console.error('Failed to mark as planted:', error);
      alert('Failed to mark as planted');
    }
  };

  const updateAssignmentStatus = async (assignmentId: string, newStatus: string, notes?: string) => {
    try {
      const token = localStorage.getItem('workerToken');
      const response = await fetch(`/api/workers/orders/${assignmentId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus, notes })
      });

      if (response.ok) {
        loadWorkerData(); // Reload data
      } else {
        alert('Failed to update assignment status');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update assignment status');
    }
  };

  const handleFileSelect = (assignmentId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);
    setSelectedFiles(prev => ({ ...prev, [assignmentId]: filesArray }));

    // Create preview URLs
    const urls = filesArray.map(file => URL.createObjectURL(file));
    setPreviewUrls(prev => ({ ...prev, [assignmentId]: urls }));
  };

  const uploadPhotos = async (assignmentId: string) => {
    const files = selectedFiles[assignmentId];
    if (!files || files.length === 0) return;

    setUploadingPhotos(assignmentId);
    const formData = new FormData();

    files.forEach(file => {
      formData.append('photos', file);
    });

    try {
      const token = localStorage.getItem('workerToken');
      const response = await fetch(`/api/workers/orders/${assignmentId}/photos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        // Clean up preview URLs
        previewUrls[assignmentId]?.forEach(url => URL.revokeObjectURL(url));
        setSelectedFiles(prev => {
          const newState = { ...prev };
          delete newState[assignmentId];
          return newState;
        });
        setPreviewUrls(prev => {
          const newState = { ...prev };
          delete newState[assignmentId];
          return newState;
        });
        loadWorkerData(); // Reload to show new photos
        alert('Photos uploaded successfully!');
      } else {
        alert('Failed to upload photos');
      }
    } catch (error) {
      console.error('Failed to upload photos:', error);
      alert('Failed to upload photos');
    } finally {
      setUploadingPhotos(null);
    }
  };

  const handlePhotoUpdateFileSelect = (updateKey: string, file: File | null) => {
    if (!file) return;

    setPhotoUpdateFiles(prev => ({ ...prev, [updateKey]: file }));

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPhotoUpdatePreviews(prev => ({ ...prev, [updateKey]: url }));
  };

  const uploadPhotoUpdate = async (packageId: string, scheduleIndex: number, updateKey: string) => {
    const file = photoUpdateFiles[updateKey];
    if (!file) return;

    setUploadingPhotoUpdate(updateKey);
    const formData = new FormData();
    formData.append('photo', file);

    try {
      const token = localStorage.getItem('workerToken');
      const response = await fetch(`/api/workers/photo-updates/${packageId}/upload/${scheduleIndex}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        // Clean up preview URL
        if (photoUpdatePreviews[updateKey]) {
          URL.revokeObjectURL(photoUpdatePreviews[updateKey]);
        }
        setPhotoUpdateFiles(prev => {
          const newState = { ...prev };
          delete newState[updateKey];
          return newState;
        });
        setPhotoUpdatePreviews(prev => {
          const newState = { ...prev };
          delete newState[updateKey];
          return newState;
        });
        loadWorkerData(); // Reload to show updated status
        alert('Photo update uploaded successfully!');
      } else {
        alert('Failed to upload photo update');
      }
    } catch (error) {
      console.error('Failed to upload photo update:', error);
      alert('Failed to upload photo update');
    } finally {
      setUploadingPhotoUpdate(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('workerToken');
    setIsAuthenticated(false);
    setWorkerInfo(null);
    setAssignments([]);
    setPhotoUpdates([]);
    setStats({ totalAssignments: 0, treesPlanted: 0, completedOrders: 0, pendingAssignments: 0 });
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'assigned': { background: '#fff3cd', color: '#856404' },
      'planted': { background: '#d1ecf1', color: '#0c5460' },
      'completed': { background: '#d1e7dd', color: '#0a3622' }
    };
    return colors[status as keyof typeof colors] || { background: '#e9ecef', color: '#495057' };
  };

  if (!isAuthenticated) {
    return (
      <div className="portal-container" style={{ maxWidth: '500px', margin: '0 auto', padding: '20px', marginTop: '50px' }}>
        <div className="login-form" style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ textAlign: 'center', marginBottom: '2rem', color: '#333' }}>
            {'Worker Login'}
          </h2>
          
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                {'Worker ID'}
              </label>
              <input
                type="text"
                value={loginData.workerId}
                onChange={(e) => setLoginData({ ...loginData, workerId: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                {'Password'}
              </label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                background: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          {/* Portal Links for Customers and Admins */}
          <div style={{
            textAlign: 'center',
            marginTop: '1.5rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e0e0e0'
          }}>
            <p style={{
              fontSize: '0.85rem',
              color: '#666',
              marginBottom: '0.5rem'
            }}>
              {'Not a worker?'}
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem',
              alignItems: 'center'
            }}>
              <a
                href="/customer-portal"
                style={{
                  fontSize: '0.85rem',
                  color: '#666',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#228B22'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
              >
                {'Customer Login'} →
              </a>
              <span style={{ color: '#ddd' }}>|</span>
              <a
                href="/admin-dashboard"
                style={{
                  fontSize: '0.85rem',
                  color: '#666',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#228B22'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
              >
                {'Admin Login'} →
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <UniversalNavbar 
        userType="worker"
        user={workerInfo ? { name: workerInfo.name, email: workerInfo.email } : undefined}
        onLogout={handleLogout}
        
      />
      <div className="portal-container" style={{ maxWidth: '500px', margin: '0 auto', padding: '20px', marginTop: '100px' }}>
        {/* Dashboard Header */}
        <div style={{
          background: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '15px',
          marginBottom: '1.5rem',
          textAlign: 'center'
        }}>
          <h1 style={{ margin: 0, marginBottom: '0.5rem' }}>
            {'Portal del Trabajador'}
          </h1>
          {workerInfo && (
            <>
              <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>
                {'Gestiona tus asignaciones de plantación de árboles'}
              </p>
              <p style={{ margin: 0, opacity: 0.8, fontSize: '0.85rem', marginTop: '0.5rem' }}>
                {workerInfo.email} • ID: {workerInfo.workerId}
              </p>
            </>
          )}
        </div>

      {/* Stats Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '10px',
          boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#228B22' }}>
            {stats.pendingAssignments}
          </div>
          <div style={{ color: '#666', marginTop: '0.5rem', fontSize: '0.9rem' }}>
            {'Pending'}
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '10px',
          boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#228B22' }}>
            {stats.treesPlanted}
          </div>
          <div style={{ color: '#666', marginTop: '0.5rem', fontSize: '0.9rem' }}>
            {'Trees Planted'}
          </div>
        </div>
      </div>

      {/* Assignments Section */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '15px',
        boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#333', marginBottom: '1rem', textAlign: 'center' }}>
          {'My Assignments'}
        </h2>

        {assignments.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
            {'No assignments yet'}
          </p>
        ) : (
          assignments.map((assignment) => (
            <div
              key={assignment._id}
              style={{
                border: '1px solid #eee',
                padding: '1rem',
                marginBottom: '1rem',
                borderRadius: '10px',
                background: '#fafafa'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>
                  Order #{assignment._id.slice(-6)}
                </span>
                <span
                  style={{
                    padding: '3px 10px',
                    borderRadius: '15px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    ...getStatusColor(assignment.status)
                  }}
                >
                  {({'pending':'Pendiente','assigned':'Asignado','planted':'Plantado','completed':'Completado'} as Record<string,string>)[assignment.status] || assignment.status}
                </span>
              </div>

              <div style={{ marginBottom: '0.5rem' }}>
                <strong style={{ fontSize: '0.9rem' }}>
                  {assignment.treesOrdered || assignment.trees || 0} {'trees'}
                </strong>
                <span style={{ color: '#666', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                  {'for'} {assignment.customerEmail}
                </span>
              </div>

              {assignment.location && (
                <div style={{ marginBottom: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                  <strong>{'Location'}:</strong> {assignment.location}
                </div>
              )}

              <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '1rem' }}>
                <strong>{'Assigned'}:</strong> {new Date(assignment.assignedDate || assignment.assignedAt || '').toLocaleDateString()}
              </div>

              {/* Action buttons */}
              <div style={{ marginBottom: '1rem' }}>
                {assignment.status === 'assigned' && (
                  <button
                    onClick={() => openGpsModalForPlanting(assignment._id)}
                    style={{
                      background: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      marginRight: '0.5rem'
                    }}
                  >
                    {'Mark as Planted'}
                  </button>
                )}
                {assignment.status === 'planted' && (
                  <>
                    <button
                      onClick={() => {
                        // Check if at least one photo has been uploaded
                        if (!assignment.photos || assignment.photos.length === 0) {
                          alert('Please upload at least one photo of the planted tree before marking as complete');
                          return;
                        }
                        updateAssignmentStatus(assignment._id, 'completed');
                      }}
                      disabled={!assignment.photos || assignment.photos.length === 0}
                      style={{
                        background: (!assignment.photos || assignment.photos.length === 0) ? '#ccc' : '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '5px',
                        cursor: (!assignment.photos || assignment.photos.length === 0) ? 'not-allowed' : 'pointer',
                        fontSize: '0.8rem',
                        marginRight: '0.5rem',
                        opacity: (!assignment.photos || assignment.photos.length === 0) ? 0.6 : 1
                      }}
                    >
                      {'Mark Complete'}
                    </button>
                    {(!assignment.photos || assignment.photos.length === 0) && (
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#dc3545',
                        marginTop: '0.5rem',
                        fontWeight: '600'
                      }}>
                        {'⚠️ Photo required to complete'}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Photo upload */}
              {(assignment.status === 'planted' || assignment.status === 'completed') && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#333', display: 'block', marginBottom: '0.5rem' }}>
                    {'Upload Tree Photos'}
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileSelect(assignment._id, e.target.files)}
                    disabled={uploadingPhotos === assignment._id}
                    style={{ fontSize: '0.8rem', width: '100%', marginBottom: '0.5rem' }}
                  />

                  {/* Preview selected files */}
                  {previewUrls[assignment._id] && previewUrls[assignment._id].length > 0 && (
                    <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                      <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.3rem' }}>
                        Selected: {previewUrls[assignment._id].length} photo(s)
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                        {previewUrls[assignment._id].map((url, index) => (
                          <img
                            key={index}
                            src={url}
                            alt={`Preview ${index + 1}`}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '5px',
                              border: '2px solid #228B22'
                            }}
                          />
                        ))}
                      </div>
                      <button
                        onClick={() => uploadPhotos(assignment._id)}
                        disabled={uploadingPhotos === assignment._id}
                        style={{
                          background: '#228B22',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '5px',
                          cursor: uploadingPhotos === assignment._id ? 'not-allowed' : 'pointer',
                          fontSize: '0.8rem',
                          opacity: uploadingPhotos === assignment._id ? 0.7 : 1
                        }}
                      >
                        {uploadingPhotos === assignment._id ?
                          'Uploading...' :
                          'Upload Photos'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Display existing photos */}
              {assignment.photos && assignment.photos.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#333', marginBottom: '0.5rem' }}>
                    {'Current Photos'}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {assignment.photos.map((photo, index) => {
                      // Handle both string and object photo formats
                      const photoFilename = typeof photo === 'string' ? photo : (photo as any).filename || photo;
                      return (
                        <img
                          key={index}
                          src={`/api/photos/${photoFilename}`}
                          alt={`Tree ${index + 1}`}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '5px',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(`/api/photos/${photoFilename}`, '_blank')}
                          onError={(e) => {
                            console.error('Failed to load photo:', photoFilename);
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Photo Updates Section */}
      {photoUpdates.length > 0 && (
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
          marginTop: '1.5rem'
        }}>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#333', marginBottom: '1rem', textAlign: 'center' }}>
            📸 Scheduled Photo Updates
          </h2>

          {photoUpdates.map((update) => {
            const updateKey = `${update.packageId}-${update.scheduleIndex}`;
            const isOverdue = update.status === 'overdue';
            const isPending = update.status === 'pending' || update.status === 'assigned';
            const isCompleted = update.status === 'completed';

            return (
              <div
                key={updateKey}
                style={{
                  border: isOverdue ? '2px solid #dc3545' : '1px solid #eee',
                  padding: '1rem',
                  marginBottom: '1rem',
                  borderRadius: '10px',
                  background: isOverdue ? '#fff5f5' : isCompleted ? '#f0f8f0' : '#fafafa'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <span style={{ fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>
                    Order #{update.originalOrder._id.slice(-6)}
                  </span>
                  <span
                    style={{
                      padding: '3px 10px',
                      borderRadius: '15px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      background: isOverdue ? '#dc3545' : isPending ? '#ffc107' : '#28a745',
                      color: 'white'
                    }}
                  >
                    {isOverdue ? '⚠️ OVERDUE' : isCompleted ? '✅ COMPLETED' : update.status.toUpperCase()}
                  </span>
                </div>

                <div style={{ marginBottom: '0.5rem' }}>
                  <strong style={{ fontSize: '0.9rem', color: '#228B22' }}>
                    {update.description}
                  </strong>
                </div>

                <div style={{ marginBottom: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                  <strong>Customer:</strong> {update.customerEmail}
                </div>

                <div style={{ marginBottom: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                  <strong>Scheduled:</strong> {new Date(update.scheduledDate).toLocaleDateString()}
                  {isOverdue && (
                    <span style={{ color: '#dc3545', marginLeft: '0.5rem', fontWeight: '600' }}>
                      (Past Due!)
                    </span>
                  )}
                </div>

                {/* Photo upload for non-completed updates */}
                {!isCompleted && (
                  <div style={{ marginTop: '1rem' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#333', display: 'block', marginBottom: '0.5rem' }}>
                      Upload Update Photo
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        handlePhotoUpdateFileSelect(updateKey, file);
                      }}
                      disabled={uploadingPhotoUpdate === updateKey}
                      style={{ fontSize: '0.8rem', width: '100%', marginBottom: '0.5rem' }}
                    />

                    {/* Preview selected file */}
                    {photoUpdatePreviews[updateKey] && (
                      <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                        <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.3rem' }}>
                          Preview:
                        </div>
                        <img
                          src={photoUpdatePreviews[updateKey]}
                          alt="Preview"
                          style={{
                            width: '100px',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: '5px',
                            border: '2px solid #228B22',
                            marginBottom: '0.5rem'
                          }}
                        />
                        <br />
                        <button
                          onClick={() => uploadPhotoUpdate(update.packageId, update.scheduleIndex, updateKey)}
                          disabled={uploadingPhotoUpdate === updateKey}
                          style={{
                            background: '#228B22',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '5px',
                            cursor: uploadingPhotoUpdate === updateKey ? 'not-allowed' : 'pointer',
                            fontSize: '0.8rem',
                            opacity: uploadingPhotoUpdate === updateKey ? 0.7 : 1
                          }}
                        >
                          {uploadingPhotoUpdate === updateKey ?
                            'Uploading...' :
                            'Upload Photo'}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Display completed photo */}
                {isCompleted && update.photoFilename && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#333', marginBottom: '0.5rem' }}>
                      Completed Photo:
                    </div>
                    <img
                      src={`/api/photos/${update.photoFilename}`}
                      alt="Update photo"
                      style={{
                        width: '100px',
                        height: '100px',
                        objectFit: 'cover',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        border: '2px solid #28a745'
                      }}
                      onClick={() => window.open(`/api/photos/${update.photoFilename}`, '_blank')}
                      onError={(e) => {
                        console.error('Failed to load photo:', update.photoFilename);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* GPS Modal */}
      {showGpsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '15px',
            maxWidth: '500px',
            width: '100%',
            padding: '1.5rem',
            position: 'relative'
          }}>
            {/* Modal Header */}
            <div style={{
              marginBottom: '1.5rem',
              borderBottom: '2px solid #228B22',
              paddingBottom: '1rem'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#333' }}>
                {'GPS Coordinates Required'}
              </h3>
              <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                {'Capture or enter the tree planting location'}
              </p>
            </div>

            {/* GPS Error */}
            {gpsError && (
              <div style={{
                background: '#f8d7da',
                color: '#721c24',
                padding: '0.75rem',
                borderRadius: '8px',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                {gpsError}
              </div>
            )}

            {/* Auto-detect GPS Button */}
            <div style={{ marginBottom: '1.5rem' }}>
              <button
                onClick={getCurrentLocation}
                disabled={gpsLoading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: gpsLoading ? '#6c757d' : 'linear-gradient(135deg, #17a2b8 0%, #20c997 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: gpsLoading ? 'not-allowed' : 'pointer',
                  opacity: gpsLoading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {gpsLoading ? (
                  <>
                    <span style={{ animation: 'spin 1s linear infinite' }}>⟳</span>
                    {'Detecting Location...'}
                  </>
                ) : (
                  <>
                    <span>📍</span>
                    {'Use Current Location'}
                  </>
                )}
              </button>
            </div>

            {/* Manual Entry */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{
                textAlign: 'center',
                color: '#666',
                fontSize: '0.9rem',
                marginBottom: '1rem',
                fontWeight: '600'
              }}>
                {'OR Enter Manually'}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  {'Latitude'}
                </label>
                <input
                  type="number"
                  step="0.000001"
                  value={gpsCoordinates.latitude}
                  onChange={(e) => setGpsCoordinates({ ...gpsCoordinates, latitude: e.target.value })}
                  placeholder="e.g., 4.123456"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  {'Longitude'}
                </label>
                <input
                  type="number"
                  step="0.000001"
                  value={gpsCoordinates.longitude}
                  onChange={(e) => setGpsCoordinates({ ...gpsCoordinates, longitude: e.target.value })}
                  placeholder="e.g., -74.123456"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              paddingTop: '1rem',
              borderTop: '1px solid #eee'
            }}>
              <button
                onClick={() => {
                  setShowGpsModal(false);
                  setSelectedOrderForPlanting(null);
                  setGpsCoordinates({ latitude: '', longitude: '' });
                  setGpsError(null);
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}
              >
                {'Cancel'}
              </button>
              <button
                onClick={submitPlantingWithGps}
                disabled={!gpsCoordinates.latitude || !gpsCoordinates.longitude}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: (!gpsCoordinates.latitude || !gpsCoordinates.longitude)
                    ? '#ccc'
                    : 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (!gpsCoordinates.latitude || !gpsCoordinates.longitude) ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  opacity: (!gpsCoordinates.latitude || !gpsCoordinates.longitude) ? 0.6 : 1
                }}
              >
                {'Mark as Planted'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add spinning animation for loading indicator */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
    </>
  );
};

export default WorkerPortal;